// Phase 59.2 — auto-linkify text. Detects URLs, emails, and phone numbers
// and returns a React-renderable array of strings + <a> elements.
//
// Detection order: URL > email > phone (URLs may contain digits/dots that
// look like phones; emails contain '@' that would confuse domain matching).
//
// Trailing punctuation (.,;:!?)] etc) is excluded from the captured link so
// "Visit https://x.com." renders as <a>https://x.com</a> + ".".
import React from "react";

// Regex catalogue. Each pattern, when matched against the full input, yields
// a single token that we wrap in an <a>. We use a single MEGA-regex with
// alternation so the leftmost match wins and we don't double-match across
// classes.
//
// Phone formats we accept:
//   +<6..15 digits>            international, optional spaces/dashes/dots
//   0<8..10 digits>            Irish local (with separators allowed)
// We require at least 7 digits total to avoid matching "1.2" or "B-000321".
const URL_RX        = /\bhttps?:\/\/[^\s<>"')]+/i;
const BARE_DOMAIN_RX = /\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:com|net|org|io|app|ie|co|uk|edu|gov|info|biz|me|dev|page|xyz|store|tech|online|site|shop|gl|maps)\b(?:\/[^\s<>"')]*)?/i;
const EMAIL_RX      = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;
// Phone: + followed by 6-14 digits/sep OR a 0-prefixed sequence with at
// least 8 digits including separators. Total digit count is sanity-checked
// after the regex match.
const PHONE_RX      = /(?:\+\d[\d\s().-]{5,20}|\b0\d[\d\s().-]{7,18})/;

const COMBINED_RX = new RegExp(
  `(${URL_RX.source})|(${EMAIL_RX.source})|(${PHONE_RX.source})|(${BARE_DOMAIN_RX.source})`,
  "gi",
);

// Strip trailing punctuation that's almost-never part of a URL/email/phone.
const TRAILING_PUNCT_RX = /[.,;:!?)\]'"»\u201d]+$/;

function stripTrailing(token) {
  let trail = "";
  let cur = token;
  let m;
  while ((m = cur.match(TRAILING_PUNCT_RX))) {
    trail = m[0] + trail;
    cur = cur.slice(0, -m[0].length);
  }
  return [cur, trail];
}

function digitCount(s) {
  let n = 0;
  for (const ch of s) if (ch >= "0" && ch <= "9") n++;
  return n;
}

function classify(token) {
  // Re-evaluate the token in isolation. URL > email > phone > bare-domain.
  if (URL_RX.test(token)) return "url";
  if (EMAIL_RX.test(token)) return "email";
  if (PHONE_RX.test(token)) {
    // Sanity-check digit count to avoid "0123" matches.
    const d = digitCount(token);
    if (d < 7) return null;
    return "phone";
  }
  if (BARE_DOMAIN_RX.test(token)) return "domain";
  return null;
}

function buildHref(kind, token) {
  switch (kind) {
    case "url":
      return token;
    case "domain":
      return `https://${token}`;
    case "email":
      return `mailto:${token}`;
    case "phone": {
      // Keep leading + if present, then digits only.
      const hasPlus = token.trim().startsWith("+");
      const digits = token.replace(/\D/g, "");
      return `tel:${hasPlus ? "+" : ""}${digits}`;
    }
    default:
      return token;
  }
}

const LINK_CLASS = "text-blue-600 underline hover:text-blue-700 break-words";

/**
 * Linkify a string into an array of React children (strings + <a> elements).
 *
 * @param {string} text
 * @returns {Array<string|React.ReactElement>} ready to render directly.
 *
 * Returns an empty array for falsy input. Preserves original whitespace
 * (intended to be wrapped in `whitespace-pre-line` or similar).
 */
export function linkifyText(text) {
  if (!text || typeof text !== "string") return [];
  const out = [];
  let lastIdx = 0;
  // Reset lastIndex so re-runs don't bleed state.
  COMBINED_RX.lastIndex = 0;
  let m;
  while ((m = COMBINED_RX.exec(text)) !== null) {
    const start = m.index;
    const raw = m[0];
    if (!raw) {
      // Zero-width safety break.
      COMBINED_RX.lastIndex++;
      continue;
    }
    const [token, trail] = stripTrailing(raw);
    if (!token) {
      // The whole thing was punctuation; treat as plain text.
      out.push(raw);
      lastIdx = start + raw.length;
      continue;
    }
    const kind = classify(token);
    if (!kind) {
      out.push(raw);
      lastIdx = start + raw.length;
      continue;
    }
    // Push the prefix.
    if (start > lastIdx) out.push(text.slice(lastIdx, start));
    const href = buildHref(kind, token);
    const props = {
      key: `lk-${start}-${kind}`,
      href,
      className: LINK_CLASS,
      "data-link-kind": kind,
    };
    if (kind === "url" || kind === "domain") {
      props.target = "_blank";
      props.rel = "noopener noreferrer";
    }
    out.push(React.createElement("a", props, token));
    if (trail) out.push(trail);
    lastIdx = start + raw.length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
}

/**
 * Convenience: render a string with linkified content inside a span,
 * preserving whitespace via CSS class. Returns a React element.
 */
export function LinkifiedText({ text, className = "", ...rest }) {
  return React.createElement(
    "span",
    { className, ...rest },
    ...linkifyText(text),
  );
}

export default linkifyText;
